import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/parent/invite/${token}`);
        setInfo(data);
      } catch (e) {
        setPageError(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is invalid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/parent/invite/${token}`, { password });
      localStorage.setItem("vlog_parent_token", data.token);
      toast.success("You're all set!");
      nav("/parent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-5" data-testid="parent-invite-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logerd-logo.png" alt="Logerd" className="mx-auto h-16 w-16 object-contain mb-3" data-testid="parent-invite-logo" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Logerd</h1>
          <div className="text-sm text-slate-500">Parent app</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {loading && <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>}
          {!loading && pageError && (
            <div className="text-center py-6" data-testid="parent-invite-error">
              <AlertTriangle className="h-9 w-9 text-amber-500 mx-auto mb-3" />
              <p className="text-sm text-slate-700">{pageError}</p>
            </div>
          )}
          {!loading && info && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h2 className="font-display text-lg font-semibold text-slate-900">
                  Welcome{info.name ? `, ${info.name.split(" ")[0]}` : ""}!
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {info.kind === "care"
                    ? <>{info.school_name} invited you to follow <b>{info.student_name || "your family member"}</b>'s daily stays, notes and vitals, and message the facility.</>
                    : <>{info.school_name} invited you to message {info.student_name ? <b>{info.student_name}</b> : "your child"}'s teacher and receive school updates.</>}
                  {info.already_active && <span className="block mt-1 text-amber-700">You already have an account — setting a password here will replace the old one.</span>}
                </p>
              </div>
              <label className="block">
                <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Choose a password</span>
                <input
                  type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="parent-invite-password" placeholder="At least 6 characters"
                  className="w-full h-12 rounded-xl border border-slate-200 px-3 text-base focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                />
              </label>
              <label className="block">
                <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Confirm password</span>
                <input
                  type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="parent-invite-confirm" placeholder="Repeat password"
                  className="w-full h-12 rounded-xl border border-slate-200 px-3 text-base focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                />
              </label>
              <button
                type="submit" disabled={busy} data-testid="parent-invite-submit"
                className="w-full h-12 rounded-xl bg-primary text-white font-semibold inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-60 transition"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create my account
              </button>
              <p className="text-[11px] text-slate-500 text-center">You'll sign in with your mobile number ({info.phone_hint}) and this password.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
